
import { useEffect, useRef, useState, useCallback } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  Search,
  X,
  ChevronDown,
  Check,
  RotateCcw,
  SlidersHorizontal,
  Wallet,
} from "lucide-react";
import {
  PROPERTY_TYPES,
  STATUS_OPTIONS,
  PRICE_STEPS,
  formatPrice,
} from "../../lib/jlApi";
import { MORTGAGE_DEFAULTS, maxPriceForBudget } from "../../lib/mortgage";
import {
  type Filters,
  DEFAULT_FILTERS,
  activeFilterCount,
} from "./filterState";
import { matchSuggestions, type SearchSuggestion } from "./searchSuggestions";

interface ListingFiltersProps {
  filters: Filters;
  onChange: (f: Filters) => void;
  onReset: () => void;
  // Count homes matching a candidate filter set (for the mobile Apply button).
  getCount: (f: Filters) => Promise<number | null>;
}

const selectClass =
  "appearance-none rounded-lg border border-[var(--dune)] bg-white px-3 py-2 text-sm text-[var(--navy)] cursor-pointer focus:outline-none focus:ring-2 focus:ring-[var(--gold)]";

const BED_OPTS = [1, 2, 3, 4, 5];
const BATH_OPTS = [1, 2, 3, 4];

// ---- Shared building blocks -------------------------------------------------

function SearchBox({
  value,
  onChange,
  onSubmit,
}: {
  value: string;
  onChange: (v: string) => void;
  onSubmit: (v: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node))
        setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const suggestions: SearchSuggestion[] = open ? matchSuggestions(value) : [];

  const choose = (label: string) => {
    onChange(label);
    onSubmit(label);
    setOpen(false);
  };

  return (
    <div ref={boxRef} className="relative flex-1 min-w-[200px]">
      <Search
        size={16}
        className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--slate)]"
      />
      <input
        type="text"
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            onSubmit(value);
            setOpen(false);
          }
        }}
        placeholder="City, community, address, or MLS #"
        className="w-full rounded-lg border border-[var(--dune)] bg-white pl-9 pr-8 py-2 text-sm text-[var(--navy)] placeholder:text-[var(--slate)] focus:outline-none focus:ring-2 focus:ring-[var(--gold)]"
      />
      {value && (
        <button
          onClick={() => {
            onChange("");
            onSubmit("");
          }}
          aria-label="Clear search"
          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-[var(--slate)] hover:text-[var(--navy)]"
        >
          <X size={14} />
        </button>
      )}
      {suggestions.length > 0 && (
        <ul className="absolute z-[600] mt-1 max-h-64 w-full overflow-y-auto rounded-lg border border-[var(--dune)] bg-white py-1 shadow-lg">
          {suggestions.map((s) => (
            <li key={`${s.kind}-${s.label}`}>
              <button
                onClick={() => choose(s.label)}
                className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm text-[var(--navy)] hover:bg-[var(--sand)]"
              >
                <span>{s.label}</span>
                <span className="text-xs text-[var(--slate)]">
                  {s.kind === "town" ? "Town" : s.sublabel}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// Active filters as removable chips.
function ActiveChips({
  filters,
  update,
}: {
  filters: Filters;
  update: (patch: Partial<Filters>) => void;
}) {
  const chips: { key: string; label: string; clear: () => void }[] = [];
  if (filters.search.trim())
    chips.push({
      key: "q",
      label: `“${filters.search.trim()}”`,
      clear: () => update({ search: "" }),
    });
  if (filters.priceMin || filters.priceMax)
    chips.push({
      key: "price",
      label: `${filters.priceMin ? formatPrice(Number(filters.priceMin)) : "Any"} – ${
        filters.priceMax ? formatPrice(Number(filters.priceMax)) : "Any"
      }`,
      clear: () => update({ priceMin: "", priceMax: "" }),
    });
  if (filters.bed)
    chips.push({
      key: "bed",
      label: `${filters.bed}+ bd`,
      clear: () => update({ bed: "" }),
    });
  if (filters.bath)
    chips.push({
      key: "bath",
      label: `${filters.bath}+ ba`,
      clear: () => update({ bath: "" }),
    });
  filters.types.forEach((t) =>
    chips.push({
      key: `type-${t}`,
      label: t,
      clear: () => update({ types: filters.types.filter((x) => x !== t) }),
    })
  );
  if (filters.status !== DEFAULT_FILTERS.status) {
    const label =
      STATUS_OPTIONS.find((s) => s.value === filters.status)?.label ??
      filters.status;
    chips.push({
      key: "status",
      label,
      clear: () => update({ status: DEFAULT_FILTERS.status }),
    });
  }

  if (!chips.length) return null;

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {chips.map((c) => (
        <button
          key={c.key}
          onClick={c.clear}
          className="inline-flex items-center gap-1 rounded-full bg-[var(--sand)] px-2.5 py-1 text-xs text-[var(--navy)] hover:bg-[var(--dune)] active:scale-95 transition"
        >
          {c.label}
          <X size={12} />
        </button>
      ))}
    </div>
  );
}

// Min/max stepper row used inside the mobile sheet.
function MinStepper({
  label,
  value,
  options,
  suffix,
  onChange,
}: {
  label: string;
  value: string;
  options: number[];
  suffix: string;
  onChange: (v: string) => void;
}) {
  return (
    <div>
      <p className="mb-1.5 text-sm font-medium text-[var(--navy)]">{label}</p>
      <div className="flex flex-wrap gap-1.5">
        <button
          onClick={() => onChange("")}
          className={`min-h-[44px] min-w-[52px] rounded-lg border px-3 text-sm active:scale-95 transition ${
            value === ""
              ? "border-[var(--navy)] bg-[var(--navy)] text-white"
              : "border-[var(--dune)] text-[var(--navy)]"
          }`}
        >
          Any
        </button>
        {options.map((n) => (
          <button
            key={n}
            onClick={() => onChange(String(n))}
            className={`min-h-[44px] min-w-[52px] rounded-lg border px-3 text-sm active:scale-95 transition ${
              value === String(n)
                ? "border-[var(--navy)] bg-[var(--navy)] text-white"
                : "border-[var(--dune)] text-[var(--navy)]"
            }`}
          >
            {n}+{suffix ? ` ${suffix}` : ""}
          </button>
        ))}
      </div>
    </div>
  );
}

// "Shop by monthly payment": budget/down/rate sliders that convert to a max
// price and set the price filter. The mapping is shown transparently and the
// math is the shared mortgage math (no duplicated constants).
function AffordabilityPanel({
  onMaxPrice,
}: {
  onMaxPrice: (maxPrice: string) => void;
}) {
  const [budget, setBudget] = useState(3000);
  const [down, setDown] = useState(MORTGAGE_DEFAULTS.downPercent);
  const [rate, setRate] = useState(MORTGAGE_DEFAULTS.rate);

  // Max price for a budget, rounded to a clean $5k step for the filter value.
  const compute = (b: number, d: number, r: number) => {
    const raw = maxPriceForBudget(b, {
      ...MORTGAGE_DEFAULTS,
      downPercent: d,
      rate: r,
    });
    return Math.max(0, Math.round(raw / 5000) * 5000);
  };
  const maxPrice = compute(budget, down, rate);

  // Push the computed max up to the filter state on every change.
  const push = (b: number, d: number, r: number) =>
    onMaxPrice(String(compute(b, d, r)));

  return (
    <div className="rounded-xl border border-[var(--sand)] bg-[var(--cream)] p-3">
      <div className="mb-2 rounded-lg bg-white px-3 py-2 text-center text-sm text-[var(--navy)]">
        <span className="font-semibold">${budget.toLocaleString()}/mo</span>
        {" \u2248 "}
        <span className="font-semibold text-[var(--gold)]">
          {formatPrice(maxPrice)}
        </span>{" "}
        max
        <p className="mt-0.5 text-[11px] text-[var(--slate)]">
          at {down}% down, {rate}%, {MORTGAGE_DEFAULTS.termYears}-yr, incl. est.
          tax + ins.
        </p>
      </div>

      <label className="mb-1 flex items-center justify-between text-xs text-[var(--slate)]">
        <span>Monthly budget</span>
        <span className="font-medium text-[var(--navy)]">
          ${budget.toLocaleString()}
        </span>
      </label>
      <input
        type="range"
        min={1000}
        max={15000}
        step={100}
        value={budget}
        onChange={(e) => {
          const v = Number(e.target.value);
          setBudget(v);
          push(v, down, rate);
        }}
        className="mb-3 w-full cursor-pointer accent-[var(--gold)]"
        aria-label="Monthly budget"
      />

      <label className="mb-1 flex items-center justify-between text-xs text-[var(--slate)]">
        <span>Down payment</span>
        <span className="font-medium text-[var(--navy)]">{down}%</span>
      </label>
      <input
        type="range"
        min={0}
        max={50}
        step={1}
        value={down}
        onChange={(e) => {
          const v = Number(e.target.value);
          setDown(v);
          push(budget, v, rate);
        }}
        className="mb-3 w-full cursor-pointer accent-[var(--gold)]"
        aria-label="Down payment percent"
      />

      <label className="mb-1 flex items-center justify-between text-xs text-[var(--slate)]">
        <span>Interest rate</span>
        <span className="font-medium text-[var(--navy)]">
          {rate.toFixed(3).replace(/0+$/, "").replace(/\.$/, "")}%
        </span>
      </label>
      <input
        type="range"
        min={3}
        max={10}
        step={0.125}
        value={rate}
        onChange={(e) => {
          const v = Number(e.target.value);
          setRate(v);
          push(budget, down, v);
        }}
        className="w-full cursor-pointer accent-[var(--gold)]"
        aria-label="Interest rate"
      />
    </div>
  );
}

// ---- Desktop pill bar -------------------------------------------------------

function DesktopBar({
  filters,
  update,
  onReset,
}: {
  filters: Filters;
  update: (patch: Partial<Filters>) => void;
  onReset: () => void;
}) {
  const [typeOpen, setTypeOpen] = useState(false);
  const [affordOpen, setAffordOpen] = useState(false);
  const typeMenuRef = useRef<HTMLDivElement>(null);
  const activeCount = activeFilterCount(filters);

  useEffect(() => {
    if (!typeOpen) return;
    const onDown = (e: MouseEvent) => {
      if (typeMenuRef.current && !typeMenuRef.current.contains(e.target as Node))
        setTypeOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [typeOpen]);

  const toggleType = (t: string) =>
    update({
      types: filters.types.includes(t)
        ? filters.types.filter((x) => x !== t)
        : [...filters.types, t],
    });

  return (
    <div className="hidden lg:block border-b border-[var(--sand)] bg-white p-3 sm:p-4">
      <div className="flex flex-wrap items-center gap-2">
        <SearchBox
          value={filters.search}
          onChange={(v) => update({ search: v })}
          onSubmit={(v) => update({ search: v })}
        />

        <select
          value={filters.priceMin}
          onChange={(e) => update({ priceMin: e.target.value })}
          className={selectClass}
          aria-label="Minimum price"
        >
          <option value="">Min price</option>
          {PRICE_STEPS.map((p) => (
            <option key={p} value={p}>
              {formatPrice(p)}
            </option>
          ))}
        </select>
        <select
          value={filters.priceMax}
          onChange={(e) => update({ priceMax: e.target.value })}
          className={selectClass}
          aria-label="Maximum price"
        >
          <option value="">Max price</option>
          {PRICE_STEPS.map((p) => (
            <option key={p} value={p}>
              {formatPrice(p)}
            </option>
          ))}
        </select>

        <select
          value={filters.bed}
          onChange={(e) => update({ bed: e.target.value })}
          className={selectClass}
          aria-label="Minimum bedrooms"
        >
          <option value="">Beds</option>
          {BED_OPTS.map((n) => (
            <option key={n} value={n}>
              {n}+ bd
            </option>
          ))}
        </select>

        <select
          value={filters.bath}
          onChange={(e) => update({ bath: e.target.value })}
          className={selectClass}
          aria-label="Minimum bathrooms"
        >
          <option value="">Baths</option>
          {BATH_OPTS.map((n) => (
            <option key={n} value={n}>
              {n}+ ba
            </option>
          ))}
        </select>

        <div className="relative" ref={typeMenuRef}>
          <button
            onClick={() => setTypeOpen((o) => !o)}
            className={`${selectClass} flex items-center gap-1.5`}
            aria-haspopup="true"
            aria-expanded={typeOpen}
          >
            {filters.types.length
              ? `${filters.types.length} type${filters.types.length > 1 ? "s" : ""}`
              : "Type"}
            <ChevronDown size={14} />
          </button>
          {typeOpen && (
            <div className="absolute z-[600] mt-1 w-48 rounded-lg border border-[var(--dune)] bg-white p-1 shadow-lg">
              {PROPERTY_TYPES.map((t) => {
                const on = filters.types.includes(t);
                return (
                  <button
                    key={t}
                    onClick={() => toggleType(t)}
                    className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm text-[var(--navy)] hover:bg-[var(--sand)]"
                  >
                    <span
                      className={`flex h-4 w-4 items-center justify-center rounded border ${
                        on
                          ? "bg-[var(--navy)] border-[var(--navy)]"
                          : "border-[var(--dune)]"
                      }`}
                    >
                      {on && <Check size={11} className="text-white" />}
                    </span>
                    {t}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <select
          value={filters.status}
          onChange={(e) => update({ status: e.target.value })}
          className={selectClass}
          aria-label="Listing status"
        >
          {STATUS_OPTIONS.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </select>

        <button
          onClick={() => setAffordOpen((o) => !o)}
          className={`${selectClass} flex items-center gap-1.5 ${
            affordOpen ? "border-[var(--gold)] text-[var(--navy)]" : ""
          }`}
          aria-expanded={affordOpen}
        >
          <Wallet size={14} /> Monthly payment
        </button>

        {activeCount > 0 && (
          <button
            onClick={onReset}
            className="flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm text-[var(--slate)] hover:text-[var(--navy)] hover:bg-[var(--sand)]"
          >
            <RotateCcw size={14} /> Reset
          </button>
        )}
      </div>

      {affordOpen && (
        <div className="mt-3 max-w-sm">
          <AffordabilityPanel
            onMaxPrice={(v) => update({ priceMax: v })}
          />
        </div>
      )}

      <div className="mt-2">
        <ActiveChips filters={filters} update={update} />
      </div>
    </div>
  );
}

// ---- Mobile trigger + bottom sheet ------------------------------------------

function MobileFilters({
  filters,
  onChange,
  onReset,
  getCount,
}: ListingFiltersProps) {
  const [open, setOpen] = useState(false);
  const [affordOpen, setAffordOpen] = useState(false);
  const [pending, setPending] = useState<Filters>(filters);
  const [count, setCount] = useState<number | null>(null);
  const [counting, setCounting] = useState(false);
  const activeCount = activeFilterCount(filters);

  // Debounced count for the pending filters while the sheet is open. setState
  // lives inside the async callback (not the effect body) to avoid cascading
  // renders flagged by the React Compiler lint.
  useEffect(() => {
    if (!open) return;
    const id = setTimeout(async () => {
      setCounting(true);
      const n = await getCount(pending);
      setCount(n);
      setCounting(false);
    }, 300);
    return () => clearTimeout(id);
  }, [open, pending, getCount]);

  const openSheet = () => {
    setPending(filters);
    setOpen(true);
  };

  const update = useCallback(
    (patch: Partial<Filters>) => setPending((p) => ({ ...p, ...patch })),
    []
  );

  const toggleType = (t: string) =>
    update({
      types: pending.types.includes(t)
        ? pending.types.filter((x) => x !== t)
        : [...pending.types, t],
    });

  const apply = () => {
    onChange(pending);
    setOpen(false);
  };

  return (
    <div className="lg:hidden">
      <div className="border-b border-[var(--sand)] bg-white p-3">
        <div className="flex items-center gap-2">
          <div className="flex-1">
            <SearchBox
              value={filters.search}
              onChange={(v) => onChange({ ...filters, search: v })}
              onSubmit={(v) => onChange({ ...filters, search: v })}
            />
          </div>
          <button
            onClick={openSheet}
            className="relative flex min-h-[44px] items-center gap-1.5 rounded-lg border border-[var(--dune)] bg-white px-3 text-sm font-medium text-[var(--navy)] active:scale-95 transition"
            aria-label="Open filters"
          >
            <SlidersHorizontal size={16} />
            Filters
            {activeCount > 0 && (
              <span className="ml-0.5 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-[var(--gold)] px-1 text-xs font-semibold text-white">
                {activeCount}
              </span>
            )}
          </button>
        </div>
        <div className="mt-2">
          <ActiveChips
            filters={filters}
            update={(patch) => onChange({ ...filters, ...patch })}
          />
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setOpen(false)}
              className="fixed inset-0 z-[19500] bg-black/40"
            />
            <motion.div
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", stiffness: 320, damping: 34 }}
              className="fixed inset-x-0 bottom-0 z-[19600] flex max-h-[90vh] flex-col rounded-t-3xl bg-white shadow-2xl"
              role="dialog"
              aria-modal="true"
              aria-label="Filters"
            >
              <div className="flex items-center justify-between border-b border-[var(--sand)] px-4 py-3">
                <button
                  onClick={() => {
                    onReset();
                    setPending(DEFAULT_FILTERS);
                  }}
                  className="flex items-center gap-1 text-sm text-[var(--slate)] active:text-[var(--navy)]"
                >
                  <RotateCcw size={14} /> Reset
                </button>
                <span
                  className="text-base font-semibold text-[var(--navy)]"
                  style={{ fontFamily: "var(--font-playfair)" }}
                >
                  Filters
                </span>
                <button
                  onClick={() => setOpen(false)}
                  aria-label="Close filters"
                  className="p-1 text-[var(--slate)] active:text-[var(--navy)]"
                >
                  <X size={20} />
                </button>
              </div>

              <div className="flex-1 space-y-5 overflow-y-auto px-4 py-4">
                {/* Price */}
                <div>
                  <p className="mb-1.5 text-sm font-medium text-[var(--navy)]">
                    Price
                  </p>
                  <div className="flex items-center gap-2">
                    <select
                      value={pending.priceMin}
                      onChange={(e) => update({ priceMin: e.target.value })}
                      className={`${selectClass} min-h-[44px] flex-1`}
                      aria-label="Minimum price"
                    >
                      <option value="">No min</option>
                      {PRICE_STEPS.map((p) => (
                        <option key={p} value={p}>
                          {formatPrice(p)}
                        </option>
                      ))}
                    </select>
                    <span className="text-[var(--slate)]">–</span>
                    <select
                      value={pending.priceMax}
                      onChange={(e) => update({ priceMax: e.target.value })}
                      className={`${selectClass} min-h-[44px] flex-1`}
                      aria-label="Maximum price"
                    >
                      <option value="">No max</option>
                      {PRICE_STEPS.map((p) => (
                        <option key={p} value={p}>
                          {formatPrice(p)}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <MinStepper
                  label="Bedrooms"
                  value={pending.bed}
                  options={BED_OPTS}
                  suffix="bd"
                  onChange={(v) => update({ bed: v })}
                />
                <MinStepper
                  label="Bathrooms"
                  value={pending.bath}
                  options={BATH_OPTS}
                  suffix="ba"
                  onChange={(v) => update({ bath: v })}
                />

                {/* Property type chips */}
                <div>
                  <p className="mb-1.5 text-sm font-medium text-[var(--navy)]">
                    Property type
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {PROPERTY_TYPES.map((t) => {
                      const on = pending.types.includes(t);
                      return (
                        <button
                          key={t}
                          onClick={() => toggleType(t)}
                          className={`min-h-[44px] rounded-lg border px-3 text-sm active:scale-95 transition ${
                            on
                              ? "border-[var(--navy)] bg-[var(--navy)] text-white"
                              : "border-[var(--dune)] text-[var(--navy)]"
                          }`}
                        >
                          {t}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Status segmented control */}
                <div>
                  <p className="mb-1.5 text-sm font-medium text-[var(--navy)]">
                    Status
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {STATUS_OPTIONS.map((s) => {
                      const on = pending.status === s.value;
                      return (
                        <button
                          key={s.value}
                          onClick={() => update({ status: s.value })}
                          className={`min-h-[44px] rounded-lg border px-3 text-sm active:scale-95 transition ${
                            on
                              ? "border-[var(--navy)] bg-[var(--navy)] text-white"
                              : "border-[var(--dune)] text-[var(--navy)]"
                          }`}
                        >
                          {s.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Shop by monthly payment */}
                <div>
                  <button
                    onClick={() => setAffordOpen((o) => !o)}
                    className="flex w-full items-center justify-between rounded-lg border border-[var(--dune)] px-3 py-2.5 text-sm font-medium text-[var(--navy)] active:scale-[0.99]"
                    aria-expanded={affordOpen}
                  >
                    <span className="flex items-center gap-1.5">
                      <Wallet size={15} /> Shop by monthly payment
                    </span>
                    <ChevronDown
                      size={16}
                      className={`transition-transform ${affordOpen ? "rotate-180" : ""}`}
                    />
                  </button>
                  {affordOpen && (
                    <div className="mt-2">
                      <AffordabilityPanel
                        onMaxPrice={(v) => update({ priceMax: v })}
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Sticky apply button, above the home indicator */}
              <div className="border-t border-[var(--sand)] p-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))]">
                <button
                  onClick={apply}
                  className="w-full rounded-xl bg-[var(--navy)] py-3.5 text-center text-sm font-semibold text-white active:scale-[0.98] transition hover:bg-[var(--gold)]"
                >
                  {counting
                    ? "Counting…"
                    : count === null
                      ? "Apply filters"
                      : `Apply · Show ${count.toLocaleString()} ${count === 1 ? "home" : "homes"}`}
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function ListingFilters(props: ListingFiltersProps) {
  const update = useCallback(
    (patch: Partial<Filters>) => props.onChange({ ...props.filters, ...patch }),
    [props]
  );

  return (
    <>
      <DesktopBar
        filters={props.filters}
        update={update}
        onReset={props.onReset}
      />
      <MobileFilters {...props} />
    </>
  );
}
